import { message } from 'antd';
import { LocationItem } from '../SharedComponents/Forms/ItemsEditor';

interface RouteLocation {
  input_start_row?: string;
  input_address?: string;
  input_location_name?: string;
  location_id?: number | string;
  waiting_time?: number;
  contact_no?: string;
  items?: unknown;
  value?: number;
  name?: string;
}

interface ClonedOperation {
  Address: string;
  LocationID: number | string;
  LocationName: string;
  PostalCode: null;
  PriorityRoutes: number;
  ContactNo: string;
  WaitingTime: number;
  Items: LocationItem[];
  MinWindow: null;
  MaxWindow: null;
  Value: number;
  key: string;
  isStartingRow: boolean;
  isEndingRow: boolean;
}

interface MinimalLocation {
  location_id: number | string;
  location_name: string;
  address: string;
}

export const getLabelColor = (label: string): string | undefined => {
  if (label === 'Start') {
    return '#52c41a';
  }
  if (label === 'End') {
    return '#ff4d4f';
  }
  return undefined;
};

export const getDisplayLabel = (loc: RouteLocation, index: number, total: number): string => {
  if (index === 0) {
    return 'Start';
  }
  if (index === total - 1) {
    return 'End';
  }
  if (loc.input_start_row === 'True') {
    return 'Start';
  }
  return '';
};

const toLocationItems = (value: Record<string, unknown>): LocationItem[] =>
  Object.entries(value).map(([item_name, capacity]) => ({
    item_name,
    capacity: Math.abs(Number(capacity)),
    operation: Number(capacity) < 0 ? 'drop_off' : 'pickup',
  }));

export const parseLocationItems = (items: unknown): LocationItem[] => {
  if (!items) {
    return [];
  }
  if (Array.isArray(items)) {
    return items as LocationItem[];
  }
  if (typeof items === 'object') {
    return toLocationItems(items as Record<string, unknown>);
  }
  return [];
};

const parseClonedItems = (rawItems: unknown): LocationItem[] => {
  try {
    if (!rawItems) {
      return [];
    }
    if (Array.isArray(rawItems)) {
      return rawItems as LocationItem[];
    }
    if (typeof rawItems === 'object') {
      return toLocationItems(rawItems as Record<string, unknown>);
    }

    const parsed: unknown = JSON.parse(rawItems as string);
    if (Array.isArray(parsed)) {
      return parsed as LocationItem[];
    }
    if (typeof parsed === 'object' && parsed !== null) {
      return toLocationItems(parsed as Record<string, unknown>);
    }
    return [];
  } catch (e) {
    return [];
  }
};

export const buildClonedOperations = (
  sortedLocations: RouteLocation[],
): { operations: ClonedOperation[]; minimalLocations: MinimalLocation[] } => {
  const uniqueLocations = sortedLocations.filter(
    (loc, index, self) => index === self.findIndex((l) => l.input_address === loc.input_address),
  );
  const operations: ClonedOperation[] = uniqueLocations.map((loc, index) => ({
    Address: loc.input_address ?? '',
    LocationID: loc.location_id ?? 'N/A',
    LocationName: loc.input_location_name || 'N/A',
    PostalCode: null,
    PriorityRoutes: 0,
    WaitingTime: loc.waiting_time ?? 0,
    Items: parseClonedItems(loc.items),
    MinWindow: null,
    MaxWindow: null,
    Value: loc.value ?? 0,
    ContactNo: loc.contact_no ?? 'N/A',
    key: `${index + 1}`,
    isStartingRow: false,
    isEndingRow: false,
  }));

  const minimalLocations: MinimalLocation[] = uniqueLocations.map((loc) => ({
    location_id: loc.location_id ?? 'N/A',
    location_name: loc.name || loc.input_location_name || 'N/A',
    address: loc.input_address ?? '',
  }));

  return { operations, minimalLocations };
};

export const downloadFile = async (url: string, fileName: string): Promise<void> => {
  try {
    const response = await fetch(url);
    if (!response.ok) {
      message.error('Failed to download file');
      return;
    }
    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = fileName || 'download';
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(downloadUrl);
    message.success(`File ${fileName} downloaded successfully`);
  } catch (error) {
    message.error('Download error occurred');
  }
};
